import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { loadDecrypted, saveEncrypted } from "Common Usage/localStorageUtils";

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(() => {
    const saved = loadDecrypted("HeartConnect-Permissions");
    return saved ? JSON.parse(saved) : {};
  });

  // ✅ Load roleName from localStorage immediately (same pattern as permissions)
  const [roleName, setRoleName] = useState(() => {
    return loadDecrypted("HeartConnect-RoleName") || "";
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const staffId = loadDecrypted("HeartConnect-StaffId");

        if (!staffId) {
          setPermissions({});
          setRoleName("");
          setLoading(false);
          return;
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/staff/get-staff/${staffId}`
        );

        if (res.data.success) {
          const staff = res.data.data;
          const rolePermissions = staff?.rolePermission?.permission || {};
          const role = staff?.rolePermission?.rolename || "";

          // Update context
          setPermissions(rolePermissions);
          setRoleName(role);

          // Sync to localStorage
          saveEncrypted(
            "HeartConnect-Permissions",
            JSON.stringify(rolePermissions)
          );
          saveEncrypted("HeartConnect-RoleName", role);
        }
      } catch (error) {
        console.error("❌ Failed to load staff permissions:", error);
        setPermissions({});
        setRoleName("");
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, []);

  // Sync permissions to localStorage on manual update
  useEffect(() => {
    if (permissions) {
      saveEncrypted("HeartConnect-Permissions", JSON.stringify(permissions));
    }
  }, [permissions]);

  // Sync roleName to localStorage on manual update
  useEffect(() => {
    if (roleName) {
      saveEncrypted("HeartConnect-RoleName", roleName);
    }
  }, [roleName]);

  // ✅ Convenience boolean — components just use `isAdmin` directly
  const isAdmin =
    roleName.toLowerCase() === "admin" ||
    roleName.toLowerCase() === "superadmin" ||
    roleName.toLowerCase() === "super admin";

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        setPermissions,
        roleName,
        setRoleName,
        isAdmin,
        loading,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = () => useContext(PermissionContext);
